import { Card, CardBody } from '../components/ui/Card'
import { Callout } from '../components/ui/Callout'
import { Logo } from '../components/layout/Logo'
import { usePublicConfig } from '../lib/session'

export function SignIn() {
  const { data: config, isPending } = usePublicConfig()

  // Undefined while loading: assume enabled so the button does not flicker to a
  // disabled state on every page load.
  const signInEnabled = isPending || (config?.googleSignInEnabled ?? false)

  return (
    <div className="mx-auto flex min-h-[70dvh] w-full max-w-md items-center px-4">
      <Card className="w-full">
        <CardBody className="p-8 text-center">
          <Logo className="mx-auto h-10 w-10 text-forest-700 dark:text-forest-200" />
          <h1 className="mt-5 text-xl font-semibold tracking-tight">Sign in to your unit</h1>
          <p className="mt-2 text-sm text-[color:var(--text-muted)]">
            For Site Admins, Unit Admins, and Scouts.
          </p>

          {!signInEnabled && (
            <div className="mt-6 text-left">
              <Callout title="Google sign-in is not configured" tone="warning">
                <p>
                  This environment has no OAuth credentials, so sign-in cannot complete. Set{' '}
                  <code className="font-mono text-xs">Google:ClientId</code> and{' '}
                  <code className="font-mono text-xs">Google:ClientSecret</code>, then restart
                  the API.
                </p>
              </Callout>
            </div>
          )}

          {/*
            A real link, not a fetch: the OAuth flow is a browser redirect to Google and
            back to the callback, which XHR cannot follow.
          */}
          <a
            href="/api/auth/google/start"
            aria-disabled={!signInEnabled}
            onClick={(event) => {
              if (!signInEnabled) event.preventDefault()
            }}
            className={[
              'mt-7 flex h-12 w-full items-center justify-center gap-3 rounded-xl border',
              'border-[color:var(--surface-border)] bg-[color:var(--surface-raised)] font-medium transition',
              signInEnabled
                ? 'hover:bg-[color:var(--surface-sunken)]'
                : 'pointer-events-none opacity-50',
            ].join(' ')}
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
              <path
                fill="#4285F4"
                d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.4a5.5 5.5 0 0 1-2.4 3.6v3h3.9c2.3-2.1 3.6-5.2 3.6-8.8Z"
              />
              <path
                fill="#34A853"
                d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.9-3a7.2 7.2 0 0 1-10.7-3.8H1.3v3.1A12 12 0 0 0 12 24Z"
              />
              <path fill="#FBBC05" d="M5.3 14.3a7.2 7.2 0 0 1 0-4.6V6.6H1.3a12 12 0 0 0 0 10.8l4-3.1Z" />
              <path
                fill="#EA4335"
                d="M12 4.8c1.8 0 3.4.6 4.6 1.8l3.5-3.5A12 12 0 0 0 1.3 6.6l4 3.1A7.2 7.2 0 0 1 12 4.8Z"
              />
            </svg>
            Continue with Google
          </a>

          <p className="mt-6 text-xs leading-relaxed text-[color:var(--text-muted)]">
            Holiday Flags never sees or stores your password. If you are a flag customer, use the
            emailed sign-in link on the account page instead.
          </p>
        </CardBody>
      </Card>
    </div>
  )
}
